import * as _ from 'underscore';
import { IRemoteRefreshScheduleTypeNameFilter } from '../filters/RemoteRefreshScheduleTypeNameFilter';
import { IActionContext } from '../helpers/ActionContext';
import { RemoteRefreshScheduleEditorViewModel } from '../helpers/CronExpressionHelper';
import { L10n } from '../L10n/L10n';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { RemoteRefreshSchedules } from '../resources/RemoteRefreshSchedules';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ContentActionNotification } from './ContentActionNotification';
import { ModalService } from './ModalService';

export class RemoteRefreshScheduleExpectedDurationViewModel {

  public hours: number;
  public minutes: number;

  // The default is 24 hours
  constructor(expectedRefreshDurationInSec: number = 3600 * 24) {
    this.hours = Math.floor(expectedRefreshDurationInSec / 3600);
    this.minutes = Math.floor((expectedRefreshDurationInSec % 3600) / 60);
  }

  public static isEqual(objThis: RemoteRefreshScheduleExpectedDurationViewModel, objThat: RemoteRefreshScheduleExpectedDurationViewModel): boolean {
    return _.isEqual(objThis.hours, objThat.hours) &&
      _.isEqual(objThis.minutes, objThat.minutes);
  }

  public isEqual(that: RemoteRefreshScheduleExpectedDurationViewModel): boolean {
    return RemoteRefreshScheduleExpectedDurationViewModel.isEqual(this, that);
  }

  public static clone(objThis: RemoteRefreshScheduleExpectedDurationViewModel, objThat: RemoteRefreshScheduleExpectedDurationViewModel): RemoteRefreshScheduleExpectedDurationViewModel {
    objThis.hours = objThat.hours;
    objThis.minutes = objThat.minutes;

    return objThis;
  }

  public clone(that: RemoteRefreshScheduleExpectedDurationViewModel): RemoteRefreshScheduleExpectedDurationViewModel {
    return RemoteRefreshScheduleExpectedDurationViewModel.clone(this, that);
  }

}

export interface IEditRemoteRefreshExpectedDurationScope extends ng.IScope {
  dialogPrompt: string;
  setAmountTimePrompt: string;
  hoursPrompt: string;
  minutesPrompt: string;
  editorViewModel: RemoteRefreshScheduleExpectedDurationViewModel;
  hours: number;
  minutes: number;
}

export class EditRemoteRefreshExpectedDurationAction {
  private scope: IEditRemoteRefreshExpectedDurationScope;

  public static $inject = [
    '$translate',
    '$filter',
    'ConfirmActionDialog',
    'ContentActionNotification',
    'RemoteRefreshSchedules'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $filter: ng.IFilterService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ContentActionNotification: ContentActionNotification,
    private RemoteRefreshSchedules: RemoteRefreshSchedules) { }

  public execute(
      context: IActionContext,
      item: INamedItemInfo,
      currentExpectedDurationInfo: IRemoteRefreshScheduleInfo): ng.IPromise<IResult> {
    this.scope = <IEditRemoteRefreshExpectedDurationScope> context.scope.$new();
    this.scope.dialogPrompt = this.$translate.instant('editRemoteRefreshExpectedDurationDialogPrompt');
    this.scope.setAmountTimePrompt = this.$translate.instant('editRemoteRefreshExpectedDurationDialogSetAmountTime');
    this.scope.hoursPrompt = this.$translate.instant('editRemoteRefreshExpectedDurationDialogHours');
    this.scope.minutesPrompt = this.$translate.instant('editRemoteRefreshExpectedDurationDialogMinutes');

    // Initialize
    this.scope.editorViewModel = new RemoteRefreshScheduleExpectedDurationViewModel(currentExpectedDurationInfo.expectedRefreshDuration);
    const initialViewModel = new RemoteRefreshScheduleExpectedDurationViewModel().clone(this.scope.editorViewModel);

    const dialogInstance = this.ConfirmActionDialog.open({
      title: this.$translate.instant('editRemoteRefreshExpectedDurationDialogTitle'),
      bodyTemplateUrl: 'confirmActionDialogRemoteRefreshExpectedDuration.html',
      size: '500px',
      scope: this.scope,
      position: _.extend(ModalService.anchoredTo(context.targetElement), { reserveHeight: 600 }),
      actionName: this.$translate.instant('editRemoteRefreshExpectedDurationActionName'),
      action: () => {
        currentExpectedDurationInfo.expectedRefreshDuration = this.scope.editorViewModel.hours * 3600 + this.scope.editorViewModel.minutes * 60;
        return this.RemoteRefreshSchedules.updateExpectedDuration(item.id, currentExpectedDurationInfo);
      },
      confirmDisabled: () => {
        return (this.scope.editorViewModel.isEqual(initialViewModel));
      }
    });

    return dialogInstance.result
      .then((result: IResult) => {
        if (result.errors && result.errors.length) {
          const errorMessage = this.errorMessageFor(result.errors[0]);
          dialogInstance.showErrorMessage(errorMessage);
        } else {
          this.ContentActionNotification.notifySuccess(
            this.$translate.instant('editRemoteRefreshExpectedDurationSuccess')
          );
        }
        return result;
      })
      .finally(() => this.scope.$destroy());
  }

  private errorMessageFor(error: IError) {
    return this.$translate.instant('errorUnknown');
  }

}

Module.instance().service('EditRemoteRefreshExpectedDurationAction', EditRemoteRefreshExpectedDurationAction);
